import { LitElement, html, css } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import '@material/web/dialog/dialog.js'
import '@material/web/button/text-button.js'
import '@material/web/button/filled-button.js'
import '@material/web/list/list.js'
import '@material/web/list/list-item.js'
import '@material/web/radio/radio.js'
import type { MdDialog } from '@material/web/dialog/dialog.js'
import { planStorage } from '../../storage/plan-storage.js'
import { authService } from '../../services/auth/auth-service.js'

@customElement('plan-picker-dialog')
export class PlanPickerDialog extends LitElement {
  static override styles = css`
    :host {
      position: fixed;
      z-index: 3000;
      pointer-events: none;
    }

    md-dialog {
      --md-dialog-container-color: #1e293b;
      --md-dialog-headline-color: white;
      --md-dialog-supporting-text-color: rgba(255, 255, 255, 0.8);
      --md-sys-color-outline: rgba(255, 255, 255, 0.2);
      --md-dialog-container-shape: 12px;
      pointer-events: all;
      max-height: 80vh;
    }

    .plan-list {
      max-height: 300px;
      overflow-y: auto;
      margin: 0;
      padding: 0;
      list-style: none;
    }

    .plan-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 10px 4px;
      cursor: pointer;
      border-radius: 8px;
      transition: background 0.15s ease;
    }

    .plan-item:hover {
      background: rgba(255, 255, 255, 0.08);
    }

    .plan-item[selected] {
      background: rgba(255, 255, 255, 0.12);
    }

    .plan-name {
      color: white;
      font-size: 14px;
    }

    .plan-date {
      color: rgba(255, 255, 255, 0.5);
      font-size: 12px;
    }

    .empty {
      color: rgba(255, 255, 255, 0.5);
      font-size: 14px;
      padding: 16px 0;
      text-align: center;
    }

    md-text-button {
      --md-text-button-label-text-color: rgba(255, 255, 255, 0.7);
      --md-text-button-hover-label-text-color: white;
    }

    md-filled-button {
      --md-filled-button-container-color: #0891b2;
      --md-filled-button-label-text-color: white;
    }

    md-filled-button[disabled] {
      opacity: 0.5;
      pointer-events: none;
    }

    md-radio {
      --md-radio-icon-color: rgba(255, 255, 255, 0.5);
      --md-radio-selected-icon-color: #0891b2;
      --md-radio-hover-icon-color: rgba(255, 255, 255, 0.7);
    }
  `

  @state() private plans: Array<{ id: string; name: string; updatedAt?: string }> = []
  @state() private selectedPlanId: string | null = null
  @state() private loading = true

  private resolvePromise?: ((planId: string | null) => void) | undefined

  async show(): Promise<string | null> {
    await this.loadPlans()
    const dialog = this.shadowRoot?.querySelector('md-dialog') as MdDialog | null
    if (!dialog) return null
    return new Promise<string | null>(resolve => {
      this.resolvePromise = resolve
      void dialog.show()
    })
  }

  private async loadPlans(): Promise<void> {
    this.loading = true
    const userId = authService.getState().userId
    if (!userId) {
      this.plans = []
      this.loading = false
      return
    }
    const result = await planStorage.getPlansForUser(userId)
    this.plans =
      result.success && result.data
        ? result.data.map(p => ({ id: p.id, name: p.name, updatedAt: p.updatedAt }))
        : []
    this.loading = false
  }

  private selectPlan(planId: string): void {
    this.selectedPlanId = planId
  }

  private handleClose(e: Event): void {
    const dialog = e.target as MdDialog
    const planId = dialog.returnValue === 'confirm' ? this.selectedPlanId : null
    this.resolvePromise?.(planId)
    this.resolvePromise = undefined
  }

  override render(): unknown {
    return html`
      <md-dialog @closed=${this.handleClose}>
        <div slot="headline">Add to Plan</div>
        <div slot="content">
          ${this.loading
            ? html`<div class="empty">Loading plans...</div>`
            : this.plans.length === 0
              ? html`<div class="empty">No plans found</div>`
              : html`
                  <ul class="plan-list">
                    ${this.plans.map(
                      plan => html`
                        <li
                          class="plan-item"
                          ?selected=${this.selectedPlanId === plan.id}
                          @click=${() => this.selectPlan(plan.id)}
                        >
                          <md-radio
                            name="plan"
                            .checked=${this.selectedPlanId === plan.id}
                          ></md-radio>
                          <div>
                            <div class="plan-name">${plan.name}</div>
                            ${plan.updatedAt
                              ? html`<div class="plan-date">
                                  ${new Date(plan.updatedAt).toLocaleDateString()}
                                </div>`
                              : ''}
                          </div>
                        </li>
                      `
                    )}
                  </ul>
                `}
        </div>
        <div slot="actions">
          <md-text-button
            @click=${(): void => {
              void (this.shadowRoot?.querySelector('md-dialog') as MdDialog).close('cancel')
            }}
          >
            Cancel
          </md-text-button>
          <md-filled-button
            ?disabled=${!this.selectedPlanId}
            @click=${(): void => {
              void (this.shadowRoot?.querySelector('md-dialog') as MdDialog).close('confirm')
            }}
          >
            Add
          </md-filled-button>
        </div>
      </md-dialog>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'plan-picker-dialog': PlanPickerDialog
  }
}
